/**
 * Pre-pour — signed pours, the doses given from them, Completed, the count on
 * hand, and the holds and History pre-pour reads (main #327–#332 and #336,
 * decision D2 as revised).
 *
 * Replaces keyon's `PourAndSign`, `GetPours`, `PrePourAdminister`,
 * `GetPrePourAdministrations`, `CompleteMedication`, `GetPrePourHolds` and
 * `GetMedStatusEvents`. Main sent each with an `actorid` in the body, and
 * outside KEYON_AUTH_MODE=enforce keyon checked the privilege of whoever that
 * named; here the actor is the token, always, and nothing in a body says who
 * is acting.
 *
 * What a pour stores is the server's to decide: the request names medications,
 * days and the medication's own scheduled times; the name, the dose, which
 * slots are held and the tablets taken are read from the medication and its
 * schedule. Hold, release, change and time move are nursing's routes
 * (`medicationAdministration.js`), which pre-pour reaches with
 * `medications:prepour` as well (PP-23).
 */

import api from './client';

// --- signed pours ---------------------------------------------------------

/** The chart's pours with their items still in force, newest first. */
export function listPours(patientId, params = {}) {
  return api.get(`/patients/${patientId}/medication-pours`, params);
}

/**
 * Sign a pour: `{signature, palette: [HH:MM], skipped: [{order_medication_id,
 * name}], meds: [{order_medication_id, days: [Y-m-d], times: [HH:MM],
 * supersede?: [Y-m-d], held?: ['Y-m-d|HH:MM'], scanned_at?}]}`. Resolves to
 * `{pour, take, skipped_recorded}`: `take` is the whole tablets taken from each
 * bottle, by line; `skipped_recorded` the slots a dose already stands for,
 * which a re-pour never pours again.
 */
export async function signPour(patientId, body) {
  const response = await api.raw.post(`/patients/${patientId}/medication-pours`, body);
  const payload = response.data || {};

  return {
    pour: payload.data || null,
    take: (payload.meta && payload.meta.take) || {},
    skipped_recorded: (payload.meta && payload.meta.skipped_recorded) || [],
  };
}

// --- doses given from a pour ----------------------------------------------

export function listPourAdministrations(patientId, params = {}) {
  return api.get(`/patients/${patientId}/medication-pour-administrations`, params);
}

/**
 * Record a time block: `{witness_name, witness_user_id?, actual_time: 'HH:MM',
 * slots: [{pour_item_id, outcome: taken|except|refused, notes}]}`. All of it or
 * none; the giver is the token and the window is the server's.
 */
export function recordPourAdministration(patientId, body) {
  return api.post(`/patients/${patientId}/medication-pour-administrations`, body);
}

// --- one medication -------------------------------------------------------

/**
 * Mark a course Completed: `{completed_on: 'Y-m-d', reason}`. On or after its
 * end date (any day for a non-prescription medication); earlier is a
 * discontinue, refused as `medication_needs_discontinue`.
 */
export function completeMedication(medicationId, body) {
  return api.post(`/medications/${medicationId}/completion`, body);
}

/** Correct the count on hand: `{available_quantity, reason}` — recorded with who and why. */
export function adjustStock(medicationId, body) {
  return api.post(`/medications/${medicationId}/stock-adjustments`, body);
}

export function listStockAdjustments(medicationId) {
  return api.get(`/medications/${medicationId}/stock-adjustments`);
}

// --- what pre-pour reads about the chart ----------------------------------

/** Applied and queued holds of the current lines, from `from` (today by default). */
export function listPatientHolds(patientId, params = {}) {
  return api.get(`/patients/${patientId}/medication-holds`, params);
}

/** The chart's medication History, oldest first; `order_medication_id` narrows it to one course. */
export function listStatusEvents(patientId, params = {}) {
  return api.get(`/patients/${patientId}/medication-status-events`, params);
}
